import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Wrench, Calendar } from "lucide-react";
import { toast } from "sonner";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * VendorJumpTicketsDialog — opens when admin clicks the amber vendor-jump
 * callout on TicketSpendCard. Lists every ticket the flagged vendor closed
 * in the flagged quarter with per-ticket amount so admins can trace exactly
 * which jobs drove the spend spike.
 */
export function VendorJumpTicketsDialog({ open, onOpenChange, vendor, quarter = "current", propertyId }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!vendor) return;
    try {
      const params = { vendor, quarter };
      if (propertyId && propertyId !== "all") params.property_id = propertyId;
      const { data: d } = await api.get("/admin/tickets/spend/vendor-jump/tickets", { params });
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load tickets"); }
  }, [vendor, quarter, propertyId]);

  useEffect(() => { if (open) load(); else setData(null); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[720px] max-h-[80vh] overflow-hidden flex flex-col"
                     data-testid="vendor-jump-tickets-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-amber-400" />
            Vendor drill-down · <span className="text-orange-300">{vendor}</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {data
              ? `${data.count} ticket${data.count === 1 ? "" : "s"} closed in ${data.quarter}, ${money(data.total)} total spend.`
              : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 space-y-1.5" data-testid="vendor-jump-tickets-list">
          {!data ? (
            <div className="text-xs text-zinc-500 italic py-6 text-center">Loading tickets…</div>
          ) : data.items.length === 0 ? (
            <div className="text-xs text-zinc-500 italic py-8 text-center"
                 data-testid="vendor-jump-tickets-empty">
              No closed tickets from this vendor in {data.quarter} with a captured cost.
            </div>
          ) : data.items.map((t) => (
            <div key={t.id}
                 className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2 flex items-start gap-3"
                 data-testid={`vendor-jump-ticket-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-zinc-100 truncate">{t.title}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  {t.category && (
                    <Badge variant="outline" className="border-white/10 text-zinc-400 h-4 px-1.5 text-[9px]">
                      {t.category}
                    </Badge>
                  )}
                  <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                    <Calendar className="h-2.5 w-2.5" />
                    {(t.resolved_at || "").slice(0, 10)}
                  </span>
                </div>
              </div>
              <div className="tabular-nums text-orange-300 text-sm font-semibold shrink-0"
                   data-testid={`vendor-jump-amount-${t.id}`}>
                {money(t.amount)}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VendorJumpTicketsDialog;
